// Frozen-snapshot history — normalise what was stored into the same shape the
// live Financials tab renders.
//
// Snapshots store the RAW WIP model, not derived figures, so the numbers are
// recomputed here with computeRows/totals/summarize — the very functions the
// live tab uses. That's deliberate: deriving on the server, or duplicating the
// formulas here, would give history a second implementation of the frozen cost
// calculation that could silently disagree with the page it sits on.
//
// Four payload shapes exist in the wild and all four are handled:
//   1. { v: 2, contract, rows, forecast, budgetAdj, indirectActual, budgetSynced,
//        jobCompleted? }
//      — every snapshot since 2026-07-28. Drillable to cost code. `jobCompleted`
//        is stamped only on snapshots taken after #603 shipped; earlier ones have
//        none. See the note where it is read.
//   2. { contract, rows, forecast, budgetAdj }
//      — forecast saves from 2026-07-21 to 07-28. Drillable, but predates
//        indirectActual, so super margin equals projected margin (shown as
//        unavailable rather than as a wrong number).
//   3. { v: 2, metrics: {…roll-up row…} }
//      — the portfolio Take Snapshot button. Already derived, no cost codes.
//   4. {…roll-up row…} (bare)
//      — portfolio snapshots before 2026-07-28. Same as 3 without the wrapper.
import { computeRows, totals, summarize, type WipRow } from './wip';
import type { WipCostRow, WipSnapshot } from './apiClient';

/** The figures a history row displays. A subset of WipSummary plus actual/ctc,
 *  so both a recomputed raw model and a stored roll-up row can fill it. */
export interface HistoryMetrics {
  revised: number; billed: number; budget: number; actual: number;
  ctc: number | null;        // null for roll-up snapshots (not stored)
  eac: number;
  pctComplete: number; earned: number; overUnderBilled: number;
  projectedProfit: number; projectedMarginPct: number;
  estimateProfit: number; estimateMarginPct: number;
  fadeDollars: number; fadePts: number;
  leftToBill: number;
  /** null when the snapshot predates indirect capture — distinct from $0. */
  indirectActual: number | null;
  superProfit: number | null; superMarginPct: number | null;
}

export interface HistoryPoint {
  id: number;
  asOf: string;
  period: string;
  source: WipSnapshot['source'];
  by: string | null;
  inPeriod: number;
  metrics: HistoryMetrics;
  /** Cost-code rows, recomputed — only for raw-model snapshots. */
  rows: WipRow[] | null;
  /** False when the job had no synced estimate at the time; anything measured
   *  against the estimate is meaningless for that point. null = unknown
   *  (predates the flag), treated as known-good so old points still render. */
  budgetSynced: boolean | null;
}

const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

/** Turn one stored snapshot into a comparable point, or null if unreadable. */
export function normalizeSnapshot(s: WipSnapshot): HistoryPoint | null {
  const p = s.payload as any;
  if (!p || typeof p !== 'object') return null;

  const base = { id: s.id, asOf: s.asOf, period: s.period, source: s.source, by: s.by, inPeriod: s.inPeriod };

  // ── Raw model (shapes 1 & 2) — recompute through the live calc ─────────────
  if (Array.isArray(p.rows)) {
    // ⚠️ Release the remaining budget only when THIS snapshot says the job was
    // Completed when it was taken. History is "what we forecast then", so it must
    // not be recomputed under today's status: a job that finishes in November
    // would otherwise rewrite its own September point. Snapshots from before the
    // flag existed have none, read as not released, and keep exactly the figures
    // they always showed.
    const rows = computeRows(p.rows as WipCostRow[], {
      ctc: p.forecast ?? {}, budgetAdj: p.budgetAdj ?? {}, releaseRemaining: p.jobCompleted === true,
    });
    const t = totals(rows);
    // indirectActual is absent in shape 2. Passing 0 would render super margin
    // as EQUAL to projected margin, which looks like a real finding rather than
    // missing data — so carry the absence through as null instead.
    const hasIndirect = typeof p.indirectActual === 'number';
    const s2 = summarize(t, p.contract ?? null, hasIndirect ? p.indirectActual : 0);
    return {
      ...base,
      rows,
      budgetSynced: typeof p.budgetSynced === 'boolean' ? p.budgetSynced : null,
      metrics: {
        revised: s2.revised, billed: s2.billed, budget: s2.budget, actual: t.actual,
        ctc: t.ctc, eac: s2.eac,
        pctComplete: s2.pctComplete, earned: s2.earned, overUnderBilled: s2.overUnderBilled,
        projectedProfit: s2.projectedProfit, projectedMarginPct: s2.projectedMarginPct,
        estimateProfit: s2.estimateProfit, estimateMarginPct: s2.estimateMarginPct,
        fadeDollars: s2.fadeDollars, fadePts: s2.fadePts,
        leftToBill: s2.leftToBill,
        indirectActual: hasIndirect ? s2.indirectActual : null,
        superProfit: hasIndirect ? s2.superProfit : null,
        superMarginPct: hasIndirect ? s2.superMarginPct : null,
      },
    };
  }

  // ── Already-derived roll-up row (shapes 3 & 4) ─────────────────────────────
  const m = (p.metrics && typeof p.metrics === 'object' ? p.metrics : p) as any;
  if (m.eac == null && m.revised == null) return null;   // neither shape — unreadable
  const hasIndirect = typeof m.indirectActual === 'number';
  return {
    ...base,
    rows: null,
    budgetSynced: null,
    metrics: {
      revised: num(m.revised), billed: num(m.billed), budget: num(m.budget), actual: num(m.actual),
      ctc: null, eac: num(m.eac),
      pctComplete: num(m.pctComplete), earned: num(m.earned), overUnderBilled: num(m.overUnderBilled),
      projectedProfit: num(m.projectedProfit), projectedMarginPct: num(m.projectedMarginPct),
      estimateProfit: num(m.estimateProfit), estimateMarginPct: num(m.estimateMarginPct),
      fadeDollars: num(m.fadeDollars), fadePts: num(m.fadePts),
      leftToBill: num(m.leftToBill),
      indirectActual: hasIndirect ? num(m.indirectActual) : null,
      superProfit: hasIndirect ? num(m.superProfit) : null,
      superMarginPct: hasIndirect ? num(m.superMarginPct) : null,
    },
  };
}

export function normalizeHistory(snapshots: WipSnapshot[]): HistoryPoint[] {
  return snapshots
    .map(normalizeSnapshot)
    .filter((p): p is HistoryPoint => p !== null)
    .sort((a, b) => (a.period < b.period ? 1 : a.period > b.period ? -1 : b.asOf.localeCompare(a.asOf)));
}

/** '2026-07' → 'Jul 2026'. Built from the string, not a Date, so no timezone
 *  can shift the month (a UTC-parsed '2026-07' is 8pm on Jun 30 in ET). */
export function formatPeriod(period: string): string {
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const [y, m] = period.split('-');
  const i = Number(m) - 1;
  return i >= 0 && i < 12 ? `${MONTHS[i]} ${y}` : period;
}

export const SOURCE_LABEL: Record<string, string> = {
  'month-end': 'Month-end freeze',
  'month-close': 'Closed by accounting',
  manual: 'Manual freeze',
  forecast: 'Forecast saved',
  portfolio: 'Portfolio snapshot',
};

/** Per-cost-code movement between two raw-model points, newest first. Codes
 *  present in only one side still appear (a code added or dropped between
 *  forecasts is exactly what a reviewer needs to see). */
export interface CodeDelta {
  costCode: string;
  costCodeName: string | null;
  costTypeGroup: string;
  prevCtc: number | null; ctc: number | null; ctcDelta: number;
  prevEac: number | null; eac: number | null; eacDelta: number;
  prevActual: number | null; actual: number | null;
}
export function compareCodes(current: WipRow[], prior: WipRow[]): CodeDelta[] {
  const byCode = (rows: WipRow[]) => {
    const m = new Map<string, { ctc: number; eac: number; actual: number; name: string | null; group: string }>();
    for (const r of rows) {
      const code = r.costCode ?? '—';
      const e = m.get(code) ?? { ctc: 0, eac: 0, actual: 0, name: r.costCodeName, group: r.costTypeGroup };
      e.ctc += r.ctc; e.eac += r.eac; e.actual += r.actual ?? 0;
      if (!e.name && r.costCodeName) e.name = r.costCodeName;
      m.set(code, e);
    }
    return m;
  };
  const cur = byCode(current), pre = byCode(prior);
  const out: CodeDelta[] = [];
  for (const code of new Set([...cur.keys(), ...pre.keys()])) {
    const c = cur.get(code) ?? null, p = pre.get(code) ?? null;
    out.push({
      costCode: code,
      costCodeName: c?.name ?? p?.name ?? null,
      costTypeGroup: c?.group ?? p?.group ?? 'Other',
      prevCtc: p ? p.ctc : null, ctc: c ? c.ctc : null, ctcDelta: (c?.ctc ?? 0) - (p?.ctc ?? 0),
      prevEac: p ? p.eac : null, eac: c ? c.eac : null, eacDelta: (c?.eac ?? 0) - (p?.eac ?? 0),
      prevActual: p ? p.actual : null, actual: c ? c.actual : null,
    });
  }
  // Biggest movers first — that's what gets reviewed.
  return out.sort((a, b) => Math.abs(b.eacDelta) - Math.abs(a.eacDelta) || a.costCode.localeCompare(b.costCode));
}
